import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

type ModalDeleteConfirmationProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  boldedDescription?: string;
  itemName?: string;
  isLoading?: boolean;
  deleteButtonText?: [string, string];
};

export default function ModalDeleteConfirmation({
  open,
  onClose,
  onConfirm,
  title,
  description,
  boldedDescription,
  itemName,
  isLoading = false,
  deleteButtonText = ['Delete', 'Deleting'],
}: ModalDeleteConfirmationProps) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-[rgba(24,24,26,0.4)] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all p-6 data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="bg-white">
              <div className="sm:flex sm:items-start sm:gap-4">
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {title}
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {boldedDescription && (
                        <span className="font-semibold">
                          {boldedDescription}
                        </span>
                      )}
                      {description}
                      {itemName && (
                        <span className="font-medium text-gray-900">
                          {' '}
                          "{itemName}"
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row-reverse gap-3 mt-4">
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className="flex items-center justify-center min-w-[109px] py-[9px] px-[17px] h-[38px] text-white text-sm text-nowrap font-medium bg-rose-500 rounded-full shadow-sm hover:bg-rose-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rose-600 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? deleteButtonText[1] + '...' : deleteButtonText[0]}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex items-center py-[9px] px-[17px] h-[38px] text-gray-800 text-sm font-medium border border-gray-300 rounded-full shadow-sm hover:bg-gray-250 hover:text-gray-700 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
